import React, { useCallback, useMemo } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import {
  IntlManager,
  NotificationManager,
  useApplicationState,
  useObservableOddsContext,
} from '@atlas/core';
import { Button, Label } from '@atlas/ui';

import { useLocalBetslip } from 'common/Betslip/LocalBetslipContext';
import { useAccountBlocked } from 'hooks/useAccountBlocked';
import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';

import NoMoneyButton from './NoMoneyButton';

const PlaceBetButton = props => {
  const {
    balance,
    betAmount,
    disabled,
    hasInactiveBets,
    loading,
    onSubmit,
    selectedFreebet = {},
    vip,
  } = props;

  const { freeBetAmount } = selectedFreebet;

  const { acceptAllOdds, hasUnacceptedOdds } = useObservableOddsContext();
  const { setIsOpenPopup } = useLocalBetslip();
  const isAccountBlocked = useAccountBlocked();
  const { showSelfRegulationPopupIfNeeded, showSpentLimitPopupIfNeeded } =
    useSelfRegulationContext();

  const betAmountDecimal = IntlManager.formatMoney(freeBetAmount || betAmount);

  const {
    wallet: {
      detailedBalance: {
        BONUS: balanceBonus = 0,
        CASH: balanceCash,
        FROZEN: balanceFrozen = 0,
      } = {},
    } = {
      detailedBalance: {},
    },
  } = useApplicationState();

  const overallBalance = Number(balanceCash) + Number(balanceFrozen) + Number(balanceBonus);

  const handleSubmit = useCallback(async () => {
    if (isAccountBlocked) {
      setIsOpenPopup(false);
      NotificationManager.display({
        id: 'accountBlocked',
        type: 'accountBlocked',
      });
      return;
    }

    const canProceedSpentLimit = await showSpentLimitPopupIfNeeded();
    if (!canProceedSpentLimit) return;

    const canProceedSelfRegulation = await showSelfRegulationPopupIfNeeded();
    if (!canProceedSelfRegulation) return;

    // Accepting odds changes
    if (hasUnacceptedOdds) {
      acceptAllOdds();
    }
    // Placing bets
    onSubmit();
  }, [
    isAccountBlocked,
    showSpentLimitPopupIfNeeded,
    showSelfRegulationPopupIfNeeded,
    hasUnacceptedOdds,
    onSubmit,
    setIsOpenPopup,
    acceptAllOdds,
  ]);

  const betButtonError = React.useMemo(() => {
    if (hasInactiveBets) {
      if (betAmount) {
        return {
          message: 'remove_inactive_outcomes_to_bet_and_bet_now',
        };
      }

      return {
        message: 'remove_inactive_outcomes_to_bet',
      };
    }

    if (!freeBetAmount) {
      // If freebet not added
      // Validating amount and balance also

      if (!betAmount) {
        return {
          message: 'place_amount_to_bet',
        };
      }
    }

    return null;
  }, [betAmount, freeBetAmount, hasInactiveBets]);

  const betButtonText = useMemo(() => {
    const formattedBetAmount = betAmountDecimal;
    const params = { value: formattedBetAmount };
    if (betButtonError) {
      return <Label message={betButtonError.message} />;
    }

    if (hasUnacceptedOdds) {
      return <Label message="betslip_accept_odds_changes_and_bet_now" params={params} />;
    }

    if (vip) {
      return <Label message="make_vip_request" params={params} />;
    }

    return <Label message="bet_amount_now" params={params} />;
  }, [betAmountDecimal, betButtonError, hasUnacceptedOdds, vip]);

  const betButtonClassName = cx(hasUnacceptedOdds && 'buttonBet__acceptChanges');

  const isButtonDisabled =
    disabled ||
    loading ||
    (!!betButtonError && !hasInactiveBets && !(hasUnacceptedOdds && betAmount));

  if (overallBalance < betAmount && !freeBetAmount) {
    return <NoMoneyButton className="btn primary big" disabled={isButtonDisabled} />;
  }

  let dataTestId;

  if (loading) {
    dataTestId = 'betslip-loading';
  }

  if (!isButtonDisabled) {
    dataTestId = 'betslip-place-bet';
  }

  return (
    <Button
      className={betButtonClassName}
      dataTestId={dataTestId}
      disabled={isButtonDisabled}
      loading={loading}
      onClick={handleSubmit}
      size="big"
      type="primary"
    >
      {betButtonText}
    </Button>
  );
};

PlaceBetButton.propTypes = {
  balance: PropTypes.number.isRequired,
  betAmount: PropTypes.number,
  currency: PropTypes.any.isRequired,
  hasInactiveBets: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  selectedFreebet: PropTypes.object,
};

export default PlaceBetButton;
